import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Wallet, ArrowUpRight, ArrowDownRight, Loader2, Banknote } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { vendorApi } from '@/services/api';
import { formatNPR } from '@/utils/format';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@nepal-ecom/ui';

interface Balance {
  balance: number;
  pendingBalance: number;
  totalEarned: number;
  totalWithdrawn: number;
}

interface Transaction {
  id: string;
  type: 'credit' | 'debit' | 'pending' | 'payout' | string;
  amount: string | number;
  description?: string;
  status: string;
  createdAt: string;
}

interface Payout {
  id: string;
  amount: string | number;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | string;
  bankDetails?: { bankName?: string; accountNumber?: string };
  createdAt: string;
}

const num = (v: string | number | null | undefined) => (v == null ? 0 : typeof v === 'string' ? Number(v) : v);

const STATUS_TONE: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  pending: 'warning',
  approved: 'success',
  completed: 'success',
  rejected: 'destructive',
};

export function Finance() {
  const [balance, setBalance] = useState<Balance | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);

  const [showPayout, setShowPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [balRes, txRes, payRes] = await Promise.all([
        vendorApi.getWalletBalance(),
        vendorApi.getWalletTransactions({ limit: 20 }),
        vendorApi.getPayouts({ limit: 10 }),
      ]);
      setBalance(balRes.data?.data ?? null);
      const txPayload = txRes.data?.data;
      setTransactions(Array.isArray(txPayload) ? txPayload : txPayload?.data ?? []);
      const payPayload = payRes.data?.data;
      setPayouts(Array.isArray(payPayload) ? payPayload : payPayload?.data ?? []);
    } catch (e) {
      toast.error('Could not load wallet', { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const submitPayout = async () => {
    const amt = Number(payoutAmount);
    if (!amt || amt < 500) return toast.error('Minimum payout is रु. 500');
    if (!bankName.trim() || !accountNumber.trim()) return toast.error('Bank details required');
    setSubmitting(true);
    try {
      await vendorApi.requestPayout(amt, {
        bankName: bankName.trim(),
        accountNumber: accountNumber.trim(),
        accountName: accountName.trim() || undefined,
      });
      toast.success('Payout requested', { description: `रु. ${amt.toLocaleString()} — admin will review.` });
      setShowPayout(false);
      setPayoutAmount('');
      await load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message
        ?? (e as Error).message;
      toast.error('Payout failed', { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Finance & payouts"
        description="Your earnings, transaction history, and payout requests."
        actions={
          <Button onClick={() => setShowPayout((v) => !v)} disabled={loading || !balance || balance.balance < 500}>
            <Banknote className="h-4 w-4" /> Request payout
          </Button>
        }
      />

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Available balance</p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">{formatNPR(balance?.balance ?? 0)}</p>
                <p className="text-xs text-slate-500 mt-1">Withdrawable today</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Pending</p>
                <p className="text-2xl font-bold text-amber-700 tabular-nums">{formatNPR(balance?.pendingBalance ?? 0)}</p>
                <p className="text-xs text-slate-500 mt-1">From orders not yet delivered</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Lifetime earned</p>
                <p className="text-2xl font-bold text-emerald-700 tabular-nums">{formatNPR(balance?.totalEarned ?? 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Withdrawn</p>
                <p className="text-2xl font-bold text-slate-700 tabular-nums">{formatNPR(balance?.totalWithdrawn ?? 0)}</p>
              </CardContent>
            </Card>
          </div>

          {showPayout && (
            <Card className="mb-6">
              <CardHeader><CardTitle>Request a payout</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Amount (NPR) — min 500</label>
                  <Input type="number" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} className="mt-1" placeholder="0" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Bank name</label>
                    <Input value={bankName} onChange={(e) => setBankName(e.target.value)} className="mt-1" placeholder="Nabil Bank" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700">Account number</label>
                    <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} className="mt-1" placeholder="0123456789012" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Account holder name (optional)</label>
                  <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} className="mt-1" />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Button onClick={submitPayout} disabled={submitting}>
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit request'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowPayout(false)} disabled={submitting}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>Recent transactions</CardTitle></CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-500">No transactions yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {transactions.map((t) => {
                      const isCredit = t.type === 'credit' || (typeof t.amount === 'number' ? t.amount > 0 : Number(t.amount) > 0);
                      return (
                        <li key={t.id} className="py-3 flex items-center gap-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${isCredit ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                            {isCredit ? <ArrowDownRight className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">{t.description ?? t.type}</p>
                            <p className="text-xs text-slate-500">{new Date(t.createdAt).toLocaleString()}</p>
                          </div>
                          <p className={`text-sm font-bold tabular-nums ${isCredit ? 'text-emerald-700' : 'text-red-700'}`}>
                            {isCredit ? '+' : '−'}{formatNPR(Math.abs(num(t.amount)))}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Payout requests</CardTitle></CardHeader>
              <CardContent>
                {payouts.length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-500">No payout requests yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {payouts.map((p) => (
                      <li key={p.id} className="py-3 flex items-center gap-3">
                        <Wallet className="h-5 w-5 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-900 tabular-nums">{formatNPR(num(p.amount))}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {p.bankDetails?.bankName ?? 'Bank'}
                            {p.bankDetails?.accountNumber && ` · ${p.bankDetails.accountNumber}`}
                            {' · '}{new Date(p.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge variant={STATUS_TONE[p.status] ?? 'secondary'}>{p.status}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
